import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PackagePlus,
  ImagePlus,
  X,
  ShieldCheck,
  Tag,
  Boxes,
  Banknote,
  FileText,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const AddProduct = () => {
  const navigate = useNavigate();

  const role = localStorage.getItem("role");

  const [isAdding, setIsAdding] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    price: "",
    stock: "",
    image: null,
  });

  const categories = [
    { value: "Accessories", label: "Accessories" },
    { value: "Phones", label: "Phones" },
    { value: "Laptops", label: "Laptops" },
    { value: "Watches", label: "Watches" },
    { value: "TVs", label: "TVs" },
    { value: "Radios", label: "Radios" },
  ];

  useEffect(() => {
    if (role !== "admin") {
      Swal.fire({
        icon: "error",
        title: "Unauthorized",
        text: "You do not have permission to add products.",
        confirmButtonColor: "#2563EB",
      }).then(() => {
        navigate("/");
      });
    }
  }, [role, navigate]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      const file = files?.[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        Swal.fire({
          icon: "warning",
          title: "Invalid File",
          text: "Please select a valid image file.",
          confirmButtonColor: "#2563EB",
        });

        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "Image Too Large",
          text: "Please choose an image smaller than 5 MB.",
          confirmButtonColor: "#2563EB",
        });

        e.target.value = "";
        return;
      }

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      setImagePreview(URL.createObjectURL(file));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview(null);

    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (role !== "admin") {
      Swal.fire({
        icon: "error",
        title: "Unauthorized",
        text: "You do not have permission to add products.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!formData.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Product Name Required",
        text: "Please enter the product name.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!formData.category) {
      Swal.fire({
        icon: "warning",
        title: "Category Required",
        text: "Please select a product category.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const price = Number(formData.price);
    const stock = Number(formData.stock);

    if (!Number.isFinite(price) || price <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Price",
        text: "Please enter a valid price greater than zero.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!Number.isInteger(stock) || stock < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Stock",
        text: "Stock must be a whole number greater than or equal to zero.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!formData.image) {
      Swal.fire({
        icon: "warning",
        title: "Product Image Required",
        text: "Please upload an image for the product.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const data = new FormData();

    data.append("name", formData.name.trim());
    data.append("description", formData.description.trim());
    data.append("category", formData.category);
    data.append("price", price);
    data.append("stock", stock);
    data.append("image", formData.image);

    setIsAdding(true);

    try {
      const response = await api.post(
        "/add_product/",
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        await Swal.fire({
          icon: "success",
          title: "Product Added",
          text: `${formData.name} has been added successfully.`,
          confirmButtonColor: "#2563EB",
        });

        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }

        setFormData({
          name: "",
          description: "",
          category: "",
          price: "",
          stock: "",
          image: null,
        });

        setImagePreview(null);

        navigate("/products");
      }
    } catch (error) {
      console.error("Error adding product:", error);

      Swal.fire({
        icon: "error",
        title: "Add Product Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "There was an error adding the product. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setIsAdding(false);
    }
  };

  if (role !== "admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-8 text-sm font-semibold"
          >
            <ArrowLeft size={17} />
            Back to Products
          </button>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <ShieldCheck size={15} />
            Admin Inventory
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Add New Product
          </h1>

          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Create a new product listing for the Nexindi Shop and set its
            pricing, category, stock quantity, description, and featured image.
          </p>
        </div>
      </section>

      {/* Form */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden"
          >
            {/* Form Header */}
            <div className="px-6 md:px-9 py-6 border-b border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <PackagePlus size={22} />
                </div>

                <div>
                  <h2 className="text-xl font-bold">
                    Product Information
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Fill in the details below to add a new product.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-9 space-y-8">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Product Name
                </label>

                <div className="relative">
                  <PackagePlus
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Wireless Bluetooth Headphones"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Description
                </label>

                <div className="relative">
                  <FileText
                    size={18}
                    className="absolute left-4 top-4 text-slate-400"
                  />

                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Describe the product, its features, specifications, or important details..."
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 resize-y outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Category / Price / Stock */}
              <div className="grid md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Category
                  </label>

                  <div className="relative">
                    <Tag
                      size={17}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />

                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    >
                      <option value="">
                        Select category
                      </option>

                      {categories.map((category) => (
                        <option
                          key={category.value}
                          value={category.value}
                        >
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Price
                  </label>

                  <div className="relative">
                    <Banknote
                      size={17}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="number"
                      name="price"
                      min="1"
                      step="0.01"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      placeholder="0"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </div>

                  <p className="text-xs text-slate-400 mt-2">
                    Price in KES
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Initial Stock
                  </label>

                  <div className="relative">
                    <Boxes
                      size={17}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="number"
                      name="stock"
                      min="0"
                      step="1"
                      value={formData.stock}
                      onChange={handleChange}
                      required
                      placeholder="0"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </div>
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Product Image
                </label>

                <p className="text-xs text-slate-400 mb-4">
                  Upload a clear product image. Maximum size: 5 MB.
                </p>

                {imagePreview ? (
                  <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-100">
                    <img
                      src={imagePreview}
                      alt="Product preview"
                      className="w-full h-[300px] md:h-[420px] object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/35 via-transparent to-transparent" />

                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-slate-950/70 text-white backdrop-blur-md flex items-center justify-center hover:bg-red-600 transition"
                    >
                      <X size={18} />
                    </button>

                    <div className="absolute bottom-5 left-5">
                      <span className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-md rounded-xl px-3 py-2 text-xs font-semibold text-slate-700">
                        <ImagePlus size={14} />
                        Product Image
                      </span>
                    </div>
                  </div>
                ) : (
                  <label className="group block border-2 border-dashed border-slate-300 rounded-3xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition">
                      <ImagePlus size={25} />
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-2">
                      Upload product image
                    </h3>

                    <p className="text-sm text-slate-500">
                      Click to select an image from your device.
                    </p>

                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </label>
                )}

                {imagePreview && (
                  <label className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-blue-600 cursor-pointer hover:text-blue-700">
                    <ImagePlus size={16} />
                    Replace image

                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 md:px-9 py-6 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-xs text-slate-500 max-w-md">
                Review the product information carefully before adding it to
                the shop.
              </p>

              <button
                type="submit"
                disabled={isAdding}
                className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isAdding ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Adding Product...
                  </>
                ) : (
                  <>
                    <PackagePlus size={18} />
                    Add Product
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AddProduct;